import { distance, point } from '@turf/turf';
import mbxGeocoding from '@mapbox/mapbox-sdk/services/geocoding';
import { Dispatch } from '@reduxjs/toolkit';
import { EnglandCentreLongitude, UkFitBounds, mapbox_token } from '../../settings';
import { OrderLocation, PrintShop } from '../../utils/types';
import i18n from '../../i18nconfig';
import {
  setDistanceToShop,
  setLocationPermission,
  setSnackbar,
  setUserLocation,
  setZoom,
} from '../../store/features/app/appSlice';
import { store } from '../../store/configureStore';
import { Coordinates } from './types/MapTypes';
import { nativeAppSupports } from '../../utils/nativeApp';

// The visitor's own location from the phone or browser (it asks for permission the first time).
// If it's refused, unavailable or outside the UK (no shops or printers there), the map keeps the
// whole-UK view. Replaces the old IP lookup (ipapi.co), which often guessed the wrong city or country.
export const requestDeviceLocation = () => {
  // The native app reads the phone's location itself and answers through
  // mobileManager.locationReceived(true, { latitude, longitude }) -> applyDeviceLocation
  if (window.isMobileApp && nativeAppSupports('locationCoords')) {
    window.mobileManager.requestLocation();
    return;
  }
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    applyDeviceLocation(null);
    return;
  }
  navigator.geolocation.getCurrentPosition(
    ({ coords }) => applyDeviceLocation({ latitude: coords.latitude, longitude: coords.longitude }),
    () => applyDeviceLocation(null),
    // A city-level fix is enough for finding nearby shops; reuse one from the last 5 minutes
    { enableHighAccuracy: false, timeout: 10000, maximumAge: 5 * 60 * 1000 },
  );
};

// Use a location from the browser or the native app: in the UK, centre the map on it; otherwise
// (refused, unavailable or abroad) keep the whole-UK view
export const applyDeviceLocation = (location: Coordinates | null) => {
  if (!location || !isInUk(location)) {
    store.dispatch(setLocationPermission(false));
    return;
  }
  store.dispatch(setUserLocation(location));
  store.dispatch(setZoom(12));
  store.dispatch(setLocationPermission(true));
};

// Straight-line distance to the shop for its card: "380 meters" or "1.2 km". The real route and
// travel time come from the visitor's maps app (Directions button)
export const calculateDistance = (shopLocation: PrintShop, userLocation: Coordinates, dispatch: any) => {
  if (!shopLocation || !userLocation) {
    return;
  }

  const storePoint = point([parseFloat(shopLocation.lon), parseFloat(shopLocation.lat)]);
  const userPoint = point([userLocation.longitude, userLocation.latitude]);
  const meters = distance(userPoint, storePoint, { units: 'meters' });

  dispatch(
    setDistanceToShop({
      distance:
        meters < 1000
          ? i18n.t('map.meters', { distance: Math.max(10, Math.round(meters / 10) * 10) })
          : i18n.t('map.km', { distance: (meters / 1000).toFixed(1) }),
    }),
  );
};

// Printt only operates in the UK. A location outside it (abroad, or an IP lookup that guesses wrong)
// shouldn't move the map or be used for routes and distances.
export const isInUk = (location?: { latitude?: number; longitude?: number } | null) => {
  if (!location || typeof location.latitude !== 'number' || typeof location.longitude !== 'number') return false;
  const [[west, south], [east, north]] = UkFitBounds;
  // A little margin for coasts and islands (Scilly, Shetland)
  return (
    location.longitude >= west - 0.6 &&
    location.longitude <= east + 0.4 &&
    location.latitude >= south - 0.2 &&
    location.latitude <= north + 1.6
  );
};

// Where a shop or printer is (the API sends numbers in full_address, strings on the shop itself), or null if unknown
type Locatable = {
  full_address?: { lat?: number | string; lon?: number | string } | null;
  lat?: number | string;
  lon?: number | string;
};
export const shopCoordinates = (shop?: Locatable | null): Coordinates | null => {
  const latitude = Number(shop?.full_address?.lat || shop?.lat);
  const longitude = Number(shop?.full_address?.lon || shop?.lon);
  return shop && Number.isFinite(latitude) && Number.isFinite(longitude) ? { latitude, longitude } : null;
};

// Show the whole UK north to south. When the map is wide enough, centre England left to right,
// as long as Northern Ireland's west coast stays in view
export const fitMapToUk = (map: {
  fitBounds: Function;
  getBounds: Function;
  setCenter: Function;
  getCenter: Function;
}) => {
  map.fitBounds(UkFitBounds, { padding: 16, duration: 0 });
  const bounds = map.getBounds();
  if (!bounds) return;
  const halfWidth = (bounds.getEast() - bounds.getWest()) / 2;
  const westmost = UkFitBounds[0][0];
  const centre = Math.max(map.getCenter().lng, Math.min(EnglandCentreLongitude, westmost + halfWidth - 0.3));
  map.setCenter([centre, map.getCenter().lat]);
};

export const getCountryName = (country: string, t: any) => {
  return country === 'Bulgaria' || country === 'BG' ? t('countryPicker.bg') : t('countryPicker.uk');
};

export const geocodingClient = mbxGeocoding({ accessToken: mapbox_token || '' });

export const handleMapDisplayLocation = async (
  city: string,
  country: string,
  language: string[],
  dispatch: Dispatch,
  t: any,
) => {
  try {
    const response = await geocodingClient
      .forwardGeocode({
        query: `${city}, ${country}`, // include the city in the query
        autocomplete: false,
        limit: 1,
        countries: [country],
        language: language,
        types: ['country', 'region', 'postcode', 'place'], // include 'place' in the types to search for cities
      })
      .send();
    if (response && response.body && response.body.features && response.body.features.length > 0) {
      const [lng, lat] = response.body.features[0].center;
      dispatch(setUserLocation({ latitude: lat, longitude: lng }));
      dispatch(setZoom(8));
    } else {
      dispatch(setSnackbar({ open: true, message: t('map.noResults'), type: 'error' }));
    }
  } catch (error) {
    dispatch(setSnackbar({ open: true, message: t('map.noResultsSubtitle'), type: 'error' }));
  }
};

// TODO: Rework for BG time
